import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { encryptApiKey, decryptApiKey } from '../utils/encryption'

const Profile = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const [formData, setFormData] = useState({
    display_name: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    api_key: '',
    gemini_api_key: ''
  })

  const [showApiKey, setShowApiKey] = useState(false)
  const [showGeminiApiKey, setShowGeminiApiKey] = useState(false)

  useEffect(() => {
    if (user) {
      getProfile()
    }
  }, [user])

  const getProfile = async () => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          openai (
            api_key,
            is_active
          ),
          gemini (
            api_key,
            is_active
          )
        `)
        .eq('id', user.id)
        .single()

      if (error) throw error

      setProfile(data)
      // ถอดรหัส API key ก่อนแสดงในฟอร์ม
      const decryptedOpenAIKey = data?.openai?.[0]?.api_key ? decryptApiKey(data.openai[0].api_key) : '';
      const decryptedGeminiKey = data?.gemini?.[0]?.api_key ? decryptApiKey(data.gemini[0].api_key) : '';
      
      setFormData(prev => ({
        ...prev,
        display_name: data?.display_name || '',
        api_key: decryptedOpenAIKey,
        gemini_api_key: decryptedGeminiKey
      }))
    } catch (error) {
      console.error('Error loading profile:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    
    try {
      setUpdating(true)
      setMessage({ type: '', text: '' })

      // Update profile data
      const updates = {
        display_name: formData.display_name
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)

      if (updateError) throw updateError

      // Update OpenAI API key if provided
      if (formData.api_key) {
        // เข้ารหัส API key ก่อนบันทึกลงฐานข้อมูล
        const encryptedApiKey = encryptApiKey(formData.api_key);
        
        // Check if API key already exists
        const { data: existingKey } = await supabase
          .from('openai')
          .select('id')
          .eq('profile_id', user.id)
          .single()

        let apiKeyError
        if (existingKey) {
          // Update existing key
          const { error } = await supabase
            .from('openai')
            .update({
              api_key: encryptedApiKey,
              is_active: true
            })
            .eq('id', existingKey.id)
          apiKeyError = error
        } else {
          // Insert new key
          const { error } = await supabase
            .from('openai')
            .insert({
              profile_id: user.id,
              api_key: encryptedApiKey,
              is_active: true
            })
          apiKeyError = error
        }

        if (apiKeyError) throw apiKeyError
      }
      
      // Update Gemini API key if provided
      if (formData.gemini_api_key) {
        // เข้ารหัส API key ก่อนบันทึกลงฐานข้อมูล
        const encryptedGeminiApiKey = encryptApiKey(formData.gemini_api_key);
        
        // Check if Gemini API key already exists
        const { data: existingGeminiKey } = await supabase
          .from('gemini')
          .select('id')
          .eq('profile_id', user.id)
          .single()

        let geminiApiKeyError
        if (existingGeminiKey) {
          // Update existing key
          const { error } = await supabase
            .from('gemini')
            .update({
              api_key: encryptedGeminiApiKey,
              is_active: true
            })
            .eq('id', existingGeminiKey.id)
          geminiApiKeyError = error
        } else {
          // Insert new key
          const { error } = await supabase
            .from('gemini')
            .insert({
              profile_id: user.id,
              api_key: encryptedGeminiApiKey,
              is_active: true
            })
          geminiApiKeyError = error
        }

        if (geminiApiKeyError) throw geminiApiKeyError
      }

      // Update password if provided
      if (formData.new_password) {
        if (formData.new_password !== formData.confirm_password) {
          throw new Error('รหัสผ่านใหม่ไม่ตรงกัน')
        }

        const { error: passwordError } = await supabase.auth.updateUser({
          password: formData.new_password
        })

        if (passwordError) throw passwordError
      }

      setMessage({ type: 'success', text: 'อัพเดทข้อมูลสำเร็จ' })
      getProfile()
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    } finally {
      setUpdating(false)
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getMembershipText = (membership) => {
    switch (membership) {
      case 'free':
        return (
          <div className="flex items-center gap-2">
            <span className="text-gray-400">🆓</span>
            <span>สมาชิกฟรี</span>
          </div>
        )
      case 'vip':
        return (
          <div className="flex items-center gap-2">
            <span className="text-yellow-400">👑</span>
            <span>สมาชิก VIP</span>
          </div>
        )
      case 'lifetime':
        return (
          <div className="flex items-center gap-2">
            <span className="text-green-400">♾️</span>
            <span>สมาชิกตลอดชีพ</span>
          </div>
        )
      case 'admin':
        return (
          <div className="flex items-center gap-2">
            <span className="text-red-400">⚡</span>
            <span>ผู้ดูแลระบบ</span>
          </div>
        )
      default:
        return (
          <div className="flex items-center gap-2">
            <span>❓</span>
            <span>ไม่ระบุ</span>
          </div>
        )
    }
  }

  if (!user || loading || !profile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <main className="flex-grow flex items-center justify-center">
          <div className="text-xl">กำลังโหลด...</div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            <h1 className="text-2xl font-bold mb-6">ข้อมูลสมาชิก</h1>

            {/* Membership Info */}
            <div className="mb-8 p-4 bg-gray-800 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400">อีเมล</p>
                  <p className="text-lg">{profile?.email || user?.email}</p>
                </div>
                <div>
                  <p className="text-gray-400">ระดับสมาชิก</p>
                  <div className={`text-lg
                    ${profile?.membership === 'vip' && 'text-yellow-400'}
                    ${profile?.membership === 'lifetime' && 'text-green-400'}
                    ${profile?.membership === 'admin' && 'text-red-400'}
                  `}>
                    {getMembershipText(profile?.membership)}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400">วันที่สมัคร</p>
                  <p className="text-lg">{profile?.created_at ? formatDate(profile.created_at) : '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400">วันหมดอายุ</p>
                  <p className="text-lg">
                    {profile?.membership === 'vip' ? (
                      profile?.expires_at ? formatDate(profile.expires_at) : '-'
                    ) : profile?.membership === 'lifetime' ? (
                      <span className="text-green-400">ตลอดชีพ ♾️</span>
                    ) : profile?.membership === 'admin' ? (
                      <span className="text-red-400">ตลอดชีพ ⚡</span>
                    ) : (
                      '-'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Update Form */}
            <form onSubmit={handleUpdate} className="space-y-6">
              {message.text && (
                <div className={`mb-4 p-3 rounded ${
                  message.type === 'error' ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'
                }`}>
                  {message.text}
                </div>
              )}

              <div className="space-y-6">
                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    ชื่อที่แสดง
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* OpenAI API Key */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    OpenAI API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={formData.api_key}
                      onChange={e => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder="sk-..."
                      className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showApiKey ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-400">
                    API key จะถูกเข้ารหัสและเก็บไว้อย่างปลอดภัย
                  </p>
                </div>
                
                {/* Gemini API Key */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Gemini API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showGeminiApiKey ? "text" : "password"}
                      value={formData.gemini_api_key}
                      onChange={e => setFormData(prev => ({ ...prev, gemini_api_key: e.target.value }))}
                      placeholder="AIza..."
                      className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGeminiApiKey(!showGeminiApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showGeminiApiKey ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-400">
                    Gemini API key จะถูกเข้ารหัสและเก็บไว้อย่างปลอดภัย
                  </p>
                </div>

                {/* Change Password */}
                <div className="border-t border-gray-700 pt-6">
                  <h3 className="text-lg font-medium mb-4">เปลี่ยนรหัสผ่าน</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        รหัสผ่านใหม่
                      </label>
                      <input
                        type="password"
                        value={formData.new_password}
                        onChange={e => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        ยืนยันรหัสผ่านใหม่
                      </label>
                      <input
                        type="password"
                        value={formData.confirm_password}
                        onChange={e => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updating}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {updating ? 'กำลังอัพเดท...' : 'บันทึกการเปลี่ยนแปลง'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Profile